import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { IconButton, Tooltip, Typography } from '@material-ui/core'
import { Check, Clear, Edit, Delete, EventBusy } from '@material-ui/icons'

import useAmenitiesApi from '../../hooks/amenities/useAmenitiesApi'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import { userTypes } from '../../utils/constants'
import CustomTable from '../customTable/CustomTable'
import cookies from '../../utils/cookies'
import ListHeader from '../common/ListHeader'
import AmenityTypesManager from './AmenityTypesManager'
import AmenityUnavailabilityManager from './AmenityUnavailabilityManager'

// Fallback icon when the amenityType row has no icon column populated.
// Admin-created types created before Migration 1 get this until edited.
const FALLBACK_ICON = '🏢'

export default function AmenityList() {
  const history = useHistory()
  const classes = useStyles()
  const [typesManagerOpen, setTypesManagerOpen] = useState(false)
  const [unavailabilityManagerOpen, setUnavailabilityManagerOpen] =
    useState(false)
  const [selectedAmenity, setSelectedAmenity] = useState(null)

  const [{ amenitiesState, amenityTypesState }, amenityMethods] =
    useAmenitiesApi()
  const [{ buildingsState }, buildingMethods] = useBuildingsApi()

  const getLoggedUser = () => {
    try {
      const user = cookies.get('user')
      return JSON.parse(user)
    } catch (e) {
      return null
    }
  }
  const loggedUser = getLoggedUser()
  const isAdmin = loggedUser?.type === userTypes.admin
  const canManage =
    loggedUser?.type === userTypes.admin ||
    loggedUser?.type === userTypes.buildingAdmin

  useEffect(() => {
    amenityMethods.getAmenities(true)
    amenityMethods.getAmenityTypes()
    buildingMethods.getBuildings(true)
  }, [])

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`
    } else if (hours > 0) {
      return `${hours}h`
    } else {
      return `${mins}m`
    }
  }

  const formatCancellationPeriod = (value, unit) => {
    let unitText = 'horas'
    if (unit === 'days') unitText = 'días'
    else if (unit === 'minutes') unitText = 'minutos'
    return `${value} ${unitText}`
  }

  const handleManageUnavailability = (amenity) => {
    setSelectedAmenity(amenity)
    setUnavailabilityManagerOpen(true)
  }

  const columns = [
    {
      id: 'icon',
      label: '',
      minWidth: 50,
      format: (amenity) => (
        <Typography style={{ fontSize: '1.5rem' }}>
          {amenity.amenityType?.icon || FALLBACK_ICON}
        </Typography>
      ),
    },
    { id: 'name', label: 'Nombre', minWidth: 170 },
    {
      id: 'building',
      label: 'Edificio/Barrio',
      minWidth: 150,
      format: (amenity) => amenity.building?.name || 'N/A',
    },
    {
      id: 'amenityType',
      label: 'Tipo',
      minWidth: 120,
      format: (amenity) => amenity.amenityType?.name || 'N/A',
    },
    {
      id: 'duration',
      label: 'Duración Min/Max',
      minWidth: 150,
      format: (amenity) =>
        `${formatDuration(amenity.minDurationMinutes)} - ${formatDuration(
          amenity.maxDurationMinutes
        )}`,
    },
    {
      id: 'timeSlot',
      label: 'Fracción de Tiempo',
      minWidth: 120,
      format: (amenity) => formatDuration(amenity.timeSlotMinutes),
    },
    {
      id: 'maxReservations',
      label: 'Reservas Simultáneas',
      minWidth: 140,
      align: 'center',
      format: (amenity) => amenity.maxSimultaneousReservationsPerUser,
    },
    {
      id: 'pricePerSlot',
      label: 'Precio por Fracción',
      minWidth: 130,
      format: (amenity) =>
        amenity.pricePerSlot > 0 ? `$${amenity.pricePerSlot}` : 'Gratis',
    },
    {
      id: 'extraCost',
      label: 'Costo Adicional',
      minWidth: 120,
      format: (amenity) =>
        amenity.extraCostPerUse > 0 ? `$${amenity.extraCostPerUse}` : 'N/A',
    },
    {
      id: 'cancellationPeriod',
      label: 'Período de Cancelación',
      minWidth: 150,
      format: (amenity) =>
        formatCancellationPeriod(
          amenity.cancellationPeriodValue,
          amenity.cancellationPeriodUnit
        ),
    },
    {
      id: 'active',
      label: 'Activo',
      minWidth: 80,
      align: 'center',
      format: (amenity) =>
        amenity.active ? (
          <Tooltip title="Activo">
            <Check className={classes.activeIcon} />
          </Tooltip>
        ) : (
          <Tooltip title="Inactivo">
            <Clear className={classes.inactiveIcon} />
          </Tooltip>
        ),
    },
    {
      ...((loggedUser?.type === userTypes.admin ||
        loggedUser?.type === userTypes.buildingAdmin) && {
        id: '',
        label: '',
        minWidth: 5,
      }),
    },
  ]

  const options = (amenity) => {
    if (!canManage) return []
    return [
      {
        key: 'edit',
        label: 'Editar',
        onClick: () => {
          history.push('nueva-amenity', { amenity })
        },
      },
      {
        key: 'unavailability',
        label: 'Gestionar Disponibilidad',
        onClick: () => {
          handleManageUnavailability(amenity)
        },
      },
      {
        key: 'delete',
        label: 'Eliminar',
        onClick: async () => {
          if (
            window.confirm(
              '¿Estás seguro de que quieres eliminar este amenity?'
            )
          ) {
            try {
              await amenityMethods.deleteAmenity(amenity.id)
            } catch (error) {
              // Error handling is done in the hook
            }
          }
        },
      },
    ]
  }

  return (
    <>
      <ListHeader
        bigTitle="Gestión de Amenities"
        canCreate={canManage}
        createText="Nueva Amenity"
        createRedirect={() => history.push('/nueva-amenity')}
        secondaryButtonText={isAdmin ? 'Gestionar Tipos' : null}
        secondaryButtonRedirect={
          isAdmin ? () => setTypesManagerOpen(true) : null
        }
      />

      <CustomTable
        loading={amenitiesState.isLoading}
        columns={columns}
        data={amenitiesState.amenities || []}
        getData={(params) => {
          amenityMethods.getAmenities(true, { ...params })
        }}
        buildingFilter={true}
        building={false}
        apartmentFilter={false}
        searchFilter={'Nombre del amenity'}
        searchTitle="Nombre del amenity"
        searchPlaceholder="Buscar por nombre del amenity"
        options={options}
        serverPagination={false}
        serverCount={amenitiesState.count}
      />

      <AmenityTypesManager
        open={typesManagerOpen}
        onClose={() => setTypesManagerOpen(false)}
      />

      <AmenityUnavailabilityManager
        open={unavailabilityManagerOpen}
        onClose={() => setUnavailabilityManagerOpen(false)}
        amenity={selectedAmenity}
      />
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  activeIcon: {
    color: theme.palette.success.main,
  },
  inactiveIcon: {
    color: theme.palette.error.main,
  },
}))
